/**
 * Workspace invitations (CLAUDE.md 11.6, 12.1).
 *
 * The invitation token is shown **once**. The server stores only a hash, exactly as
 * it does for a share link, so there is no "show the link again" action to build
 * later -- and a UI that renders the fresh token as one more row in the list teaches
 * the user that it can be found again. It cannot. So the new link gets its own panel
 * above the list, with a copy button and the reason spelled out, the same shape as
 * `ShareDialog`'s minted-link panel for the same reason.
 *
 * Resending is the other half of that fact: it mints a *different* token and the
 * previous link stops working immediately. An administrator who resends "just in
 * case" while a colleague is halfway through the first link would otherwise have
 * broken it without being told.
 */
import { useState, type FormEvent } from 'react'
import { Check, Copy, Mail, RotateCw, Trash2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { ApiError } from '@/api/client'
import {
  useInvitations,
  useInviteMember,
  useResendInvitation,
  useRevokeInvitation,
  useWorkspace,
} from '@/api/workspaces'
import type { WorkspaceInvitation, WorkspaceRole } from '@/api/types'
import { useAuth } from '@/auth/AuthProvider'
import { Button } from '@/components/ui/button'
import { Card, CardBody, CardHeader, CardTitle } from '@/components/ui/card'
import { Field, FieldLabel } from '@/components/ui/field'
import { FieldInput } from '@/components/ui/input'
import { SelectField } from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { useToast } from '@/components/ui/toast'
import { formatDateTime } from '@/lib/format'

/** The server refuses `owner` on an invitation: ownership is granted, not offered. */
export const INVITABLE_ROLES: WorkspaceRole[] = ['admin', 'moderator', 'member']

/** The address the recipient opens. Built here because the server returns the token only. */
export function invitationUrl(token: string): string {
  return `${window.location.origin}/invitations/${token}`
}

export function InvitationsPanel() {
  const { t } = useTranslation()
  const { workspaceId } = useAuth()

  const { data: workspace } = useWorkspace()
  const canManage = workspace?.capabilities.includes('workspace:manage_members') ?? false
  const { data: invitations, isLoading } = useInvitations(workspaceId, canManage)

  const [minted, setMinted] = useState<{ token: string; resent: boolean } | null>(null)

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('invitations.title')}</CardTitle>
      </CardHeader>
      <CardBody className="space-y-4">
        <p className="text-sm text-text-muted">{t('invitations.subtitle')}</p>

        {minted && <MintedInvitation token={minted.token} resent={minted.resent} />}

        {canManage && <InviteForm onMinted={(token) => setMinted({ token, resent: false })} />}

        {isLoading && <Skeleton className="h-16 w-full" />}

        {canManage && !isLoading && (
          <InvitationList
            invitations={invitations ?? []}
            onResent={(token) => setMinted({ token, resent: true })}
          />
        )}

        {!canManage && <p className="text-xs text-text-muted">{t('members.read_only')}</p>}
      </CardBody>
    </Card>
  )
}

/**
 * The freshly minted link.
 *
 * Deliberately visually separate from the list and deliberately not dismissible by
 * anything except leaving the page: while it is on screen it is the only copy of the
 * credential that exists outside the recipient's mailbox.
 */
export function MintedInvitation({ token, resent }: { token: string; resent: boolean }) {
  const { t } = useTranslation()
  const [copied, setCopied] = useState(false)
  const url = invitationUrl(token)

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      // Clipboard access can be refused. The field is selectable either way, so this
      // is not worth interrupting anyone over.
    }
  }

  return (
    <div className="space-y-2 rounded-md border border-accent bg-surface-raised p-3">
      <p className="text-sm font-medium">{t('invitations.copy_now')}</p>
      <p className="text-xs text-text-muted">{t('invitations.copy_now_hint')}</p>
      {resent && <p className="text-xs text-[var(--status-warn)]">{t('invitations.resent')}</p>}
      <div className="flex gap-2">
        <input
          readOnly
          value={url}
          aria-label={t('invitations.url_label')}
          onFocus={(event) => event.target.select()}
          className="min-w-0 flex-1 rounded-md border border-border bg-surface px-2 py-1 text-xs"
        />
        <Button size="sm" onClick={copy} aria-label={t('action.copy')}>
          {copied ? <Check size={16} aria-hidden /> : <Copy size={16} aria-hidden />}
        </Button>
      </div>
    </div>
  )
}

function InviteForm({ onMinted }: { onMinted: (token: string) => void }) {
  const { t } = useTranslation()
  const { workspaceId } = useAuth()
  const { toast } = useToast()
  const invite = useInviteMember(workspaceId)

  const [email, setEmail] = useState('')
  const [role, setRole] = useState<WorkspaceRole>('member')

  const submit = async (event: FormEvent) => {
    event.preventDefault()
    if (!email.trim()) return
    try {
      const invitation = await invite.mutateAsync({ email: email.trim(), role })
      if (invitation.token) onMinted(invitation.token)
      setEmail('')
      toast({ title: t('invitations.sent'), variant: 'success' })
    } catch (error) {
      toast({
        title: t('common.error'),
        description: error instanceof ApiError ? error.localised : undefined,
        variant: 'error',
      })
    }
  }

  return (
    <form onSubmit={submit} className="space-y-3 border-t border-border pt-4">
      <div className="grid gap-3 sm:grid-cols-2">
        <Field>
          <FieldLabel>{t('invitations.email')}</FieldLabel>
          <FieldInput
            type="email"
            autoComplete="email"
            autoCapitalize="none"
            spellCheck={false}
            required
            value={email}
            onChange={(event) => setEmail(event.target.value)}
          />
        </Field>

        <SelectField
          label={t('members.role')}
          description={t(`members.role_hint.${role}`)}
          value={role}
          onChange={(event) => setRole(event.target.value as WorkspaceRole)}
        >
          {INVITABLE_ROLES.map((value) => (
            <option key={value} value={value}>
              {t(`members.role.${value}`)}
            </option>
          ))}
        </SelectField>
      </div>

      <Button type="submit" variant="primary" loading={invite.isPending}>
        <Mail size={16} aria-hidden />
        {t('invitations.send')}
      </Button>
    </form>
  )
}

function InvitationList({
  invitations,
  onResent,
}: {
  invitations: WorkspaceInvitation[]
  onResent: (token: string) => void
}) {
  const { t, i18n } = useTranslation()
  const { workspaceId } = useAuth()
  const { toast } = useToast()
  const resend = useResendInvitation(workspaceId)
  const revoke = useRevokeInvitation(workspaceId)

  if (invitations.length === 0) {
    return <p className="text-sm text-text-muted">{t('invitations.none')}</p>
  }

  const showError = (error: unknown) =>
    toast({
      title: t('common.error'),
      description: error instanceof ApiError ? error.localised : undefined,
      variant: 'error',
    })

  return (
    <ul className="space-y-2 border-t border-border pt-4">
      {invitations.map((invitation) => {
        const pending = invitation.accepted_at === null && !invitation.revoked
        return (
          <li
            key={invitation.id}
            className="flex flex-col gap-2 rounded-md border border-border p-3 sm:flex-row sm:items-center"
          >
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-medium">{invitation.email}</p>
              <p className="text-xs text-text-muted">
                {t(`members.role.${invitation.role}`)}
                {' · '}
                {/* Status never rides on colour alone: it is words here (14.2). */}
                {invitation.accepted_at
                  ? t('invitations.accepted')
                  : invitation.revoked
                    ? t('invitations.revoked')
                    : t('invitations.expires', {
                        date: formatDateTime(invitation.expires_at, i18n.language),
                      })}
              </p>
            </div>

            {pending && (
              <div className="flex gap-2">
                <Button
                  size="sm"
                  onClick={() =>
                    resend.mutate(invitation.id, {
                      onSuccess: (fresh) => {
                        if (fresh.token) onResent(fresh.token)
                      },
                      onError: showError,
                    })
                  }
                >
                  <RotateCw size={15} aria-hidden />
                  {t('invitations.resend')}
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  aria-label={t('invitations.revoke')}
                  onClick={() =>
                    revoke.mutate(invitation.id, {
                      onSuccess: () =>
                        toast({ title: t('invitations.revoked_toast'), variant: 'success' }),
                      onError: showError,
                    })
                  }
                >
                  <Trash2 size={15} aria-hidden />
                </Button>
              </div>
            )}
          </li>
        )
      })}
      <li className="text-xs text-text-muted">{t('invitations.resend_hint')}</li>
    </ul>
  )
}
